
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PenLine, LogIn, Heart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Issue, getAvailableIssues } from "@/lib/data/issue/availableIssues";
import { toast } from "sonner";
import ReadFilter from "./article/ReadFilter";
import { supabase } from "@/integrations/supabase/client";
import UserMenu from "./navbar/UserMenu";
import IssueSelector from "./navbar/IssueSelector";
import ArchivesMenu from "./navbar/ArchivesMenu";
import SearchBar from "./SearchBar";

interface NavbarProps {
  onLogoClick?: () => void;
  currentIssue?: string;
  showReadFilter?: boolean;
  filterEnabled?: boolean;
  onFilterToggle?: (enabled: boolean) => void;
  onSearch?: (query: string, wholeWords: boolean) => void;
  onClearSearch?: () => void;
  searchQuery?: string;
  wholeWords?: boolean;
  onWholeWordsChange?: (wholeWords: boolean) => void;
  showFavoritesOnly?: boolean;
  onFavoritesToggle?: () => void;
}

const Navbar = ({ 
  onLogoClick, 
  currentIssue, 
  showReadFilter = false,
  filterEnabled = false,
  onFilterToggle,
  onSearch,
  onClearSearch,
  searchQuery = "",
  wholeWords = false,
  onWholeWordsChange,
  showFavoritesOnly = false,
  onFavoritesToggle
}: NavbarProps) => {
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const location = useLocation();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(false);
  const [backIssues, setBackIssues] = useState<any[]>([]);
  const [loadingArchives, setLoadingArchives] = useState(false);
  
  const hideWriteButton = location.pathname.startsWith('/article') || location.pathname.startsWith('/edit-article');

  useEffect(() => {
    const loadIssues = async () => {
      const availableIssues = await getAvailableIssues();
      setIssues(availableIssues);
    };
    loadIssues();
  }, []);

  const getUserInitials = () => {
    if (!user) return "?";
    
    if (profile?.display_name) {
      const nameParts = profile.display_name.split(" ");
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }
      return profile.display_name.substring(0, 2).toUpperCase();
    }
    
    if (user.email) {
      const emailParts = user.email.split('@')[0].split('.');
      if (emailParts.length >= 2) {
        return `${emailParts[0][0]}${emailParts[1][0]}`.toUpperCase();
      }
      return user.email.substring(0, 2).toUpperCase();
    }
    
    return "??";
  };

  const handleLogoClick = (event: React.MouseEvent) => {
    if (onLogoClick) {
      event.preventDefault();
      onLogoClick();
    }
  };


const handleIssueChange = async (issueText: string) => {
  setLoading(true);
  try {
    // Issue selection is a *viewer preference*, not a global setting.
    // v2: write a structured record so we can distinguish explicit user choice from stale legacy values.
    localStorage.setItem(
      'selected_issue_v2',
      JSON.stringify({ issue: issueText, source: 'user', setAt: Date.now() })
    );

    // Keep legacy key for backward compatibility with older builds (will be ignored unless v2 exists).
    localStorage.setItem('selected_issue', issueText);

    toast.success(`Switched to ${issueText}`);

    // Reload so all data-fetching paths pick up the new selection.
    setTimeout(() => {
      window.location.reload();
    }, 150);
  } catch (error) {
    console.error("Error changing issue:", error);
    toast.error("Failed to change issue");
  } finally {
    setLoading(false);
  }
};



  
  const handleArchiveClick = (archiveIssue: string | null) => {
    if (archiveIssue) {
      // Treat archives like issue changes instead of opening URLs
      handleIssueChange(archiveIssue);
    } else {
      toast.warning("No issue information available for this archive");
    }
  };

  useEffect(() => {
    const fetchBackIssues = async () => {
      setLoadingArchives(true);
      try {
        const { data, error } = await supabase
          .from('back_issues')
          .select('id, display_issue, url')
          .order('id', { ascending: false });

        if (error) throw error;
        setBackIssues(data || []);
      } catch (error) {
        console.error("Error fetching back issues:", error);
        toast.error("Failed to load archives");
      } finally {
        setLoadingArchives(false);
      }
    };

    fetchBackIssues();
  }, []); // Removed the user dependency to load archives for all users

  return (
    <nav className="border-b shadow-sm py-4">
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Link 
            to="/" 
            className="text-2xl font-bold text-primary flex items-center gap-2"
            onClick={handleLogoClick}
          >
            The Klatsch
          </Link>
          {currentIssue && (
            <>
              <IssueSelector 
                currentIssue={currentIssue}
                issues={issues}
                loading={loading}
                onIssueChange={handleIssueChange}
              />
            </>
          )}
          
          {/* Removed the user condition to show archives to all users */}
            <ArchivesMenu 
              backIssues={backIssues}
              loadingArchives={loadingArchives}
            />
        </div>
        
        <div className="flex items-center gap-4">
          {user && onFavoritesToggle && (
            <Button
              variant={showFavoritesOnly ? "default" : "ghost"}
              size="icon"
              onClick={onFavoritesToggle}
              title={showFavoritesOnly ? "Show all articles" : "Show favorites only"}
              className="transition-colors"
            >
              <Heart className={`h-5 w-5 ${showFavoritesOnly ? 'fill-current' : ''}`} />
            </Button>
          )}
          {onSearch && onClearSearch && (
            <div className={`${user ? 'flex-1 max-w-lg' : 'max-w-md'}`}>
              <SearchBar
                onSearch={onSearch}
                onClear={onClearSearch}
                currentQuery={searchQuery}
                placeholder="Search articles..."
                wholeWords={wholeWords}
                onWholeWordsChange={onWholeWordsChange}
              />
            </div>
          )}
          {showReadFilter && user && onFilterToggle && (
            <ReadFilter 
              enabled={filterEnabled} 
              onToggle={onFilterToggle}
            />
          )}
          {authLoading ? (
            <div className="h-9 w-24 animate-pulse bg-secondary rounded" aria-hidden="true" />
          ) : user ? (
            <>
              {!hideWriteButton && (
                <Button 
                  asChild 
                  variant="default" 
                  className={location.pathname === '/create' ? 'opacity-50 cursor-not-allowed' : ''}
                >
                  <Link to="/create" className="flex items-center gap-2">
                    <PenLine size={18} />
                    Write Article
                  </Link>
                </Button>
              )}
              
              <UserMenu
                profile={profile}
                userEmail={user.email}
                getUserInitials={getUserInitials}
                signOut={signOut}
              />
            </>
          ) : (
            <Button asChild variant="default">
              <Link to="/auth" className="flex items-center gap-2">
                <LogIn size={18} />
                Sign In
              </Link>
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
